import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '@services/api.service';
import {
  PagedResponse,
  PolicyDetail,
  PolicyFamily,
  PolicyListItemResponse,
  PolicyListQuery,
  POLICY_FAMILIES
} from '@models/policy.model';

function routeSegmentFor(family: PolicyFamily): string {
  return POLICY_FAMILIES.find(f => f.key === family)!.routeSegment;
}

function toQueryString(query: PolicyListQuery): string {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.status) params.set('status', query.status);
  if (query.hasDraft !== undefined) params.set('hasDraft', String(query.hasDraft));
  if (query.isAssigned !== undefined) params.set('isAssigned', String(query.isAssigned));
  if (query.orderby) params.set('orderby', query.orderby);
  if (query.top !== undefined) params.set('top', String(query.top));
  if (query.skip !== undefined) params.set('skip', String(query.skip));
  if (query.maxpagesize !== undefined) params.set('maxpagesize', String(query.maxpagesize));

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Read-only wrapper around the two policy endpoints that are actually implemented today
 * (list + detail). Coverage, versions, simulate and assignments all return 501 on the backend —
 * add methods here only once their controllers have real logic.
 */
@Injectable({
  providedIn: 'root'
})
export class PolicyService {
  constructor(private api: ApiService) {}

  listPolicies(
    family: PolicyFamily,
    query: PolicyListQuery = {}
  ): Observable<PagedResponse<PolicyListItemResponse>> {
    return this.api.get<PagedResponse<PolicyListItemResponse>>(
      `/api/v1/${routeSegmentFor(family)}${toQueryString(query)}`
    );
  }

  getPolicy(family: PolicyFamily, policyId: string, version?: number): Observable<PolicyDetail> {
    const suffix = version !== undefined ? `?version=${version}` : '';
    return this.api.get<PolicyDetail>(`/api/v1/${routeSegmentFor(family)}/${policyId}${suffix}`);
  }
}
